import { Navigate, Route, Routes, useLocation } from "react-router-dom"
import Dashboard from "./pages/Dashboard"
import Home from "./pages/Home"
import { ProtectedRoute } from "./components/ProtectedRoute"
import { useAuth0 } from "@auth0/auth0-react"
import axios from "axios"
import { useEffect } from "react"
import NotFound from "./pages/NotFound"
import Loading from "./components/Loading"
import { useDispatch, useSelector } from "react-redux"
import { selectUser, setUserAsync } from "./reducers/userSlice"
import RegisterForm from "./pages/registration/RegisterForm"
import PricesForm from "./pages/registration/PricesForm"
import PickPayment from "./pages/registration/Payment"
import "react-contexify/dist/ReactContexify.css"
import ManageLicenses from "./pages/ManageLicenses"
import AccountSettings from "./pages/AccountSettings"
import {setSessionTimestamp} from "./reducers/sessionTimestampSlice"
import PayNow from "./pages/registration/PayNow"
import GenerateInvoice from "./pages/registration/PayInvoice"
import {InvitedRoute} from "./components/InvitedRoute"
import DocViewer from "./components/TipTapRTE/DocViewer"
import {getCustomerData} from "./reducers/stripeDetailsSlice"

function App() {
	const user = useSelector(selectUser)
	const dispatch = useDispatch()
	const location = useLocation()
	const queryParams = new URLSearchParams(location.search)
	const pricing = queryParams.get("pricing")
	const keyId = queryParams.get("key")
	const {
		isLoading,
		isAuthenticated,
		getAccessTokenSilently,
		user: authUser,
	} = useAuth0()

	useEffect(() => {
		dispatch(setSessionTimestamp())
		if(process.env.REACT_APP_REDUX_LOGS === "on") {
			document.title = "[BETA] Refzoom"
		}
	}, [])

	useEffect(() => {
		if (isLoading || !isAuthenticated) return

		const interceptorId = axios.interceptors.request.use(async (config) => {
			const url = config.url || ""
			// most of the time, Axios is being used to hit our server's APIs
			// because we configure calls to `[domain]/api/` to be routed to our server's ports
			// inside NGINX, all client-side API calls are formatted like `/api/service/whatever`
			// we can use the inclusion of HTTP/S to conditionally skip adding an Auth0
			// token to our request with this line
			const isExternalUrl = url.startsWith("https://") || url.startsWith("http://")

			if(isExternalUrl) {
				return config
			}

			// anything else (mainly /api/etc reqs) gets an automagic Auth header:
			const token = await getAccessTokenSilently()
			config.headers.Authorization = `Bearer ${token}`
			return config
		})

		dispatch(setUserAsync(authUser))
		dispatch (getCustomerData())

		return () => {
			axios.interceptors.request.eject(interceptorId)
		}
	}, [isLoading, isAuthenticated, getAccessTokenSilently, dispatch])

	if (isLoading) {
		return <Loading />
	}

	if (user && !user.registered && location.pathname !== "/register") {
		let uriString = ""
		if(pricing) {
			uriString += `?pricing=${pricing}`
		}
		if(keyId) {
			uriString += `&key=${keyId}`
		}

		return <Navigate to={`/register${uriString}`} />
	}

	if (user && user.registered && !user.isSubscribing && location.pathname === "/prices") {
		return <Navigate to={"/dashboard"} />
	}

	return (
		<Routes>
			<Route path="/" element={<Home />} />
			<Route path={"/invite"} element={<InvitedRoute />} />
			<Route
				path="/dashboard"
				element={<ProtectedRoute component={Dashboard} />}
			/>
			<Route
				path="/register"
				element={<ProtectedRoute component={RegisterForm} />}
			/>
			<Route path="/prices" element={<ProtectedRoute component={PricesForm} />} />
			<Route
				path="/subscribe"
				element={<ProtectedRoute component={PickPayment} />}
			/>
			<Route
				path={"/subscribe/pay-now"}
				element={<ProtectedRoute component={PayNow} /> }
			/>
			<Route
				path={"/subscribe/generate-invoice"}
				element={<ProtectedRoute component={GenerateInvoice}/>}
			/>
			<Route
				path="/account-settings"
				element={<ProtectedRoute component={AccountSettings} />}
			/>
			<Route
				path="/account-settings/subscription/licenses"
				element={<ProtectedRoute component={ManageLicenses} />}
			/>
			<Route
				path="/doc-viewer"
				element={<ProtectedRoute component={DocViewer} /> }
			/>
			<Route path="*" element={<NotFound />} />
		</Routes>
	)
}

export default App
